import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, UserPlus, Pencil, Trash2 } from "lucide-react";
import {
  fetchDependents,
  createDependent,
  updateDependent,
  deleteDependent,
} from "../apis/api.dependents";
import { queryDependentsKey } from "../apis/queryKeys";
import { useAuth } from "../lib/AuthContext";
import useBrandTranslation from "../hooks/useBrandTranslation";
import Spinner from "./common/Spinner";
import Button from "./common/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./common/Dialog";
import { Card, CardContent } from "./common/Card";
import customToast from "./common/Toaster";
import DependentForm, {
  relationshipLabel,
  toDependentFormValues,
} from "./DependentForm";
import PatientHealthHistoryDialog from "./PatientHealthHistoryDialog";

export default function DependentsTab() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const guardianId = user?.id;
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // dependiente en edición o null (alta)
  const [toDelete, setToDelete] = useState(null);

  const {
    data: dependents = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryDependentsKey(guardianId),
    queryFn: () => fetchDependents(guardianId),
    enabled: !!guardianId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryDependentsKey(guardianId) });

  const saveMutation = useMutation({
    mutationFn: (values) =>
      editing
        ? updateDependent(editing.id, guardianId, values)
        : createDependent(guardianId, values),
    onSuccess: () => {
      invalidate();
      customToast(
        editing
          ? t("app.dependents.updated", "Familiar actualizado")
          : t("app.dependents.created", "Familiar agregado"),
        "success"
      );
      closeDialog();
    },
    onError: (err) => {
      customToast(
        err.message || t("app.dependents.saveError", "Error al guardar el familiar"),
        "danger"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteDependent(id, guardianId),
    onSuccess: () => {
      invalidate();
      customToast(t("app.dependents.deleted", "Familiar eliminado"), "success");
      setToDelete(null);
    },
    onError: (err) => {
      customToast(
        err.message || t("app.dependents.deleteError", "Error al eliminar"),
        "danger"
      );
    },
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (dep) => {
    setEditing(dep);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const formDefaults = toDependentFormValues(editing);

  return (
    <div className="space-y-6 pb-16">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t("app.dependents.title", "Familiares")}
          </h2>
          <p className="text-sm text-gray-600">
            {t(
              "app.dependents.intro",
              "Registrá a tus hijos o familiares para poder sacarles turnos en su nombre."
            )}
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <UserPlus className="h-4 w-4 mr-2 inline" />
          {t("app.dependents.add", "Agregar familiar")}
        </Button>
      </div>

      {isLoading && <Spinner className="w-10 h-10 mx-auto" />}
      {error && (
        <p className="text-red-600 text-sm">
          {t("app.dependents.loadError", "No se pudieron cargar los familiares.")}
        </p>
      )}

      {!isLoading && !error && dependents.length === 0 && (
        <Card className="border border-dashed border-slate-200 bg-slate-50/80">
          <CardContent className="py-10 text-center text-sm text-gray-500">
            <Users className="h-10 w-10 mx-auto mb-2 text-slate-400" />
            {t(
              "app.dependents.emptyState",
              "Todavía no agregaste familiares. Usá el botón de arriba para agregar el primero."
            )}
          </CardContent>
        </Card>
      )}

      {dependents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dependents.map((dep) => (
            <Card key={dep.id} className="border border-slate-200 shadow-sm">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {dep.name} {dep.last_name}
                    </p>
                    {dep.relationship && (
                      <p className="text-xs text-slate-500">
                        {relationshipLabel(dep.relationship, t)}
                      </p>
                    )}
                    {dep.dni && (
                      <p className="text-xs text-slate-500">
                        {t("app.dependents.dni", "DNI")}: {dep.dni}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(dep)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-200 hover:bg-red-50"
                      onClick={() => setToDelete(dep)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Evolución de los datos de salud del familiar (mismos snapshots que un paciente) */}
                <PatientHealthHistoryDialog
                  patientId={dep.id}
                  patientName={`${dep.name || ""} ${dep.last_name || ""}`.trim()}
                  triggerVariant="outline"
                  triggerSize="xs"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Alta / edición */}
      <Dialog open={dialogOpen} onOpenChange={(o) => (o ? null : closeDialog())}>
        <DialogContent className="w-[92vw] max-w-md p-0 flex flex-col max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {editing
                ? t("app.dependents.editTitle", "Editar familiar")
                : t("app.dependents.addTitle", "Agregar familiar")}
            </DialogTitle>
          </DialogHeader>
          <DependentForm
            key={editing?.id || "new"}
            defaultValues={formDefaults}
            onSubmit={(values) => saveMutation.mutate(values)}
            onCancel={closeDialog}
            submitting={saveMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <Dialog open={!!toDelete} onOpenChange={(o) => (o ? null : setToDelete(null))}>
        <DialogContent className="w-[92vw] max-w-sm">
          <DialogTitle className="text-lg font-semibold text-gray-900 mb-3">
            {t("app.dependents.deleteTitle", "Eliminar familiar")}
          </DialogTitle>
          <p className="text-sm text-gray-600 mb-5">
            {t(
              "app.dependents.deleteConfirm",
              "¿Estás seguro que deseas eliminar a {{name}}? Sus turnos ya reservados no se cancelan automáticamente.",
              { name: toDelete ? `${toDelete.name} ${toDelete.last_name}` : "" }
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setToDelete(null)}
              disabled={deleteMutation.isPending}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMutation.mutate(toDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t("app.dependents.deleting", "Eliminando…")
                : t("app.dependents.delete", "Eliminar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
